import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  WidgetRecipientChooserV0ArgsDataValue,
  WidgetRecipientChooserV0PreviewDto,
  WidgetRecipientChooserV0QueryMapped,
  WidgetRecipientChooserV0Value
} from './models/widget-recipient-chooser-v0-preview.dto';
import { IoniconsFilled } from '@pt/pt-models';
import { Observable, of } from 'rxjs';
import { WidgetDialogService } from '../../../services/widget-dialog.service';

@Component({
  selector: 'pt-widget-recipient-chooser-v0-preview',
  template: `

    <ng-container *ngIf='loading; then LOADING; else PREVIEW'></ng-container>

    <ng-template #LOADING>
      Loading ...
    </ng-template>

    <ng-template #PREVIEW>

      <ng-container *ngIf='noData; then NODATA; else DATA'></ng-container>

      <ng-template #NODATA>
        <pt-widget-nodata [noDataLabel]="noDataLabel"></pt-widget-nodata>
      </ng-template>

      <ng-template #DATA>
        <div class="overflow-auto">
          <table class='table table-striped table-sm m-0'>
            <thead class='bg-secondary text-white'>
            <tr>
              <th scope='col' class='recipient-col-fix'>{{ previewData?.argsHeader?.recipient }}</th>
              <th scope='col' class="w120-fix">{{ previewData?.argsHeader?.recipientType }}</th>
              <th scope='col' class='w200-fix'>{{ previewData?.argsHeader?.extraUe }}</th>
              <th scope='col' class="w200-fix">{{ previewData?.argsHeader?.guarantees }}</th>
              <th scope='col' class='w-100'>{{ previewData?.argsHeader?.purpose }}</th>
            </tr>
            </thead>
            <tbody>
            <tr *ngFor='let row of previewRow; index as i '>
              <td>{{ row.recipient }}</td>

              <td>{{ row.recipientType }}</td>

              <td>
                <div class="d-flex align-items-center">
                  <pt-icon *ngIf='row.extraUe' [key]='IconCheckFilled'
                           class='d-inline-flex widget-icon-preview-color'></pt-icon>
                  <pt-icon *ngIf='!row.extraUe' [key]='IconUnCheckFilled'
                           class='d-inline-flex widget-icon-preview-color-disabled'></pt-icon>
                  <div *ngIf='row.country' class="d-inline-flex">
                    {{ row.country }}
                  </div>
                </div>
              </td>

              <td>
                <ul class='list-unstyled m-0'>
                  <ng-container *ngFor='let item of row.guarantees'>
                    <li>
                      <pt-icon [key]='IconCheckFilled' class='widget-icon-preview-color'></pt-icon>
                      <span>{{ item }}</span>
                    </li>
                  </ng-container>
                </ul>
              </td>

              <td>
                <div class='rc-textarea' #element [ngStyle]="{'height': getHeight(row),
              '-webkit-line-clamp': row?.guarantees?.length ? row?.guarantees?.length : 1}">
                  {{ row.purpose }}
                  <div *ngIf='isOverflow(element)'
                       [ngStyle]="{'background': i % 2 === 0 ? '#f2f2f2' : '#ffffff'}"
                       (click)='openTextArea(previewData.argsHeader.purpose, row.purpose)'
                       class='more-button'>
                    ...
                    <button class='btn btn-sm btn-link'>
                      <span class="text-lowercase">{{ "LABELS.MORE" | translate }}</span>
                    </button>
                  </div>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </ng-template>
    </ng-template>
  `,
  styles: [
    `

      .recipient-col-fix {
        max-width: 250px;
        width: 250px;
        min-width: 250px;
      }
      .more-button {
        position: absolute;
        right: 8px;
        height: 16px;
        font-size: 14px;
        line-height: 14px;
        bottom: 3px;
        color: var(--bs-primary);
        padding: 0 0 0 5px;
        background: #f2f2f2;
      }

      .more-button button {
        margin: 0;
        padding: 0;
        border: 0;
      }

      .rc-textarea {
        position: relative;
        /*max-width: 200px;*/
        display: -webkit-box;
        padding: 0 6px;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;
        min-height: 63px;
      }

      .w200-fix {
        max-width: 200px;
        width: 200px;
        min-width: 200px;
      }

      .w170-fix {
        max-width: 170px;
        width: 170px;
        min-width: 170px;
      }

      .w120-fix {
        max-width: 120px;
        width: 120px;
        min-width: 120px;
        min-height: 21px;
      }

    `
  ]
})
export class WidgetRecipientChooserV0PreviewComponent implements OnChanges {

  @Input() noDataLabel?: string;
  @Input() previewData: WidgetRecipientChooserV0PreviewDto;
  @Input() loading: boolean;
  noData = true;
  IconCheckFilled = IoniconsFilled.checkbox;
  IconUnCheckFilled = IoniconsFilled.checkboxUnchecked;
  previewRow: WidgetRecipientChooserV0Value[];

  constructor(private widgetDialogService: WidgetDialogService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.previewData) {
      this.noData = !(this.previewData?.value?.length > 0);

      if (!this.noData) {
        this.loadPreviewRow();
      }

    }
  }

  isOverflow(element) {
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
  }

  openTextArea(title: string, value: string) {
    const dialog = this.widgetDialogService.runTextArea(
      of(title),
      value,
      false);
    dialog.subscribe();
  }

  getHeight(data: WidgetRecipientChooserV0Value) {
    if (!!data && data?.guarantees?.length > 0) {
      return (data?.guarantees?.length * 21) + 'px';
    }
    return '21px';
  }

  private loadPreviewRow() {
    this.previewRow = [];
    this.previewData?.value?.forEach((row: WidgetRecipientChooserV0Value) => {
      this.previewRow.push({
        recipient: this.getRecipientLabel(row.recipient, this.previewData.recipientEntity),
        recipientType: this.getRecipientTypeLabel(row.recipientType, this.previewData.argsData.recipientType),
        purpose: row.purpose,
        country: row.country,
        extraUe: row.extraUe,
        guarantees: this.getGuaranteesLabel(row.guarantees, this.previewData.argsData.guarantees)
      } as WidgetRecipientChooserV0Value);
    });
  }

  private getRecipientLabel(recipient: string, recipientEntity: WidgetRecipientChooserV0QueryMapped[]): string {
    const entity = recipientEntity?.find(r => r.uuid === recipient);
    if (entity?.label) {
      return entity?.label;
    }
    return recipient;
  }

  private getRecipientTypeLabel(recipientType: string, recipientTypes: WidgetRecipientChooserV0ArgsDataValue[]): string {
    const currentRecipientType = recipientTypes.find(r => r.uuid === recipientType);
    if (currentRecipientType?.label) {
      return currentRecipientType?.label;
    }
    return recipientType;
  }

  private getGuaranteesLabel(guarantees: string[], guaranteesData: WidgetRecipientChooserV0ArgsDataValue[]): string[] {
    const response = [];
    guarantees?.forEach((g: string) => {
      const currentGuarante = guaranteesData?.find(current => current.uuid === g);
      if (currentGuarante?.label) {
        response.push(currentGuarante?.label);
      } else {
        // console.error('Invalid guarante with UUID ' + g + '. Value not found into recipe!');
      }
    });
    return response;
  }
}
